import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { ArrowLeft, Users, School, TrendingUp, BarChart3, Target, Award, BookOpen, RefreshCw, FileCheck, GraduationCap, AlertTriangle, Calendar, ClipboardCheck, Filter, Search, X, MapPin, Download, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Area, AreaChart
} from 'recharts';

const COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
  '#E7E9ED', '#7C4DFF', '#00E676', '#FF5252', '#448AFF', '#69F0AE',
  '#FF4081', '#18FFFF', '#FFAB40', '#B388FF', '#64FFDA', '#FF8A80',
  '#82B1FF', '#A7FFEB', '#FFD180', '#CF93FF', '#84FFFF', '#FF80AB'
];
const MOCK_COLOR = '#3b82f6';  // Blue for mock exams
const FINAL_COLOR = '#22c55e'; // Green for final exams

// Moved outside component to fix react/no-unstable-nested-components warning
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="font-medium text-gray-800">{label || payload[0]?.payload?.name || payload[0]?.payload?.fullName}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            {entry.name.includes('Rate') || entry.name.includes('Score') ? '%' : ''}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Branch school enrollment tooltip
const BranchEnrollmentTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="font-medium text-gray-800">{data.branch}</p>
        <p className="text-sm text-gray-600">{data.fullSchoolName}</p>
        <p className="text-sm text-[#5f7c8a]">Enrolled: {data.count}</p>
      </div>
    );
  }
  return null;
};

const StatisticsContent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [schools, setSchools] = useState([]);
  const [filteredStats, setFilteredStats] = useState(null);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [filters, setFilters] = useState({
    school_id: 'all',
    exam_type: 'all',
    start_date: '',
    end_date: ''
  });
  
  // Export states
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSchool, setExportSchool] = useState('all');

  // Export students data function
  const exportStudentsData = async (format = 'csv') => {
    setExportLoading(true);
    try {
      const url = exportSchool === 'all' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/students`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/students/by-school/${exportSchool}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      
      if (format === 'csv') {
        // Convert to CSV
        const headers = ['Student ID', 'Name', 'Email', 'Phone', 'Branch', 'Constituency', 'Status', 'Enrolled Schools', 'Assigned Teachers', 'Created At', 'Approved At'];
        const csvRows = [headers.join(',')];
        
        data.students.forEach(student => {
          const row = [
            `"${student.student_id || ''}"`,
            `"${student.name || ''}"`,
            `"${student.email || ''}"`,
            `"${student.phone || ''}"`,
            `"${student.branch || ''}"`,
            `"${student.constituency || ''}"`,
            `"${student.approval_status || ''}"`,
            `"${student.enrolled_schools || ''}"`,
            `"${student.assigned_teachers || ''}"`,
            `"${student.created_at || ''}"`,
            `"${student.approved_at || ''}"`
          ];
          csvRows.push(row.join(','));
        });
        
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const schoolName = data.school_name || 'All_Schools';
        link.href = URL.createObjectURL(blob);
        link.download = `students_export_${schoolName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
      } else {
        // JSON format
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        const schoolName = data.school_name || 'All_Schools';
        link.href = URL.createObjectURL(blob);
        link.download = `students_export_${schoolName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchSchools = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statistics/schools-list`);
      if (response.ok) {
        const data = await response.json();
        setSchools(data.schools || []);
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
    }
  }, []);

  const fetchFilteredStats = useCallback(async () => {
    setLoadingFiltered(true);
    try {
      const params = new URLSearchParams();
      if (filters.school_id && filters.school_id !== 'all') params.append('school_id', filters.school_id);
      if (filters.exam_type && filters.exam_type !== 'all') params.append('exam_type', filters.exam_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statistics/filtered?${params}`);
      if (response.ok) {
        const data = await response.json();
        setFilteredStats(data);
      }
    } catch (err) {
      console.error('Error fetching filtered stats:', err);
    } finally {
      setLoadingFiltered(false);
    }
  }, [filters]);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statistics/summary`);
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      const data = await response.json();
      setStats(data);
      setLastUpdated(data.last_updated || new Date().toISOString());
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  

  const fetchAttendanceStats = useCallback(async () => {
    setLoadingAttendance(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statistics/attendance`);
      if (response.ok) {
        const data = await response.json();
        setAttendanceStats(data);
      }
    } catch (err) {
      console.error('Error fetching attendance statistics:', err);
    } finally {
      setLoadingAttendance(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
    fetchAttendanceStats();
    fetchSchools();

    // Refresh when user returns to the tab or focuses the window
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStatistics();
        fetchAttendanceStats();
      }
    };
    const handleFocus = () => {
      fetchStatistics();
      fetchAttendanceStats();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchStatistics, fetchAttendanceStats, fetchSchools]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchFilteredStats();
  };

  const clearFilters = () => {
    setFilters({
      school_id: 'all',
      exam_type: 'all',
      start_date: '',
      end_date: ''
    });
    setFilteredStats(null);
  };

  const hasActiveFilters = filters.school_id !== 'all' || filters.exam_type !== 'all' || filters.start_date || filters.end_date;

  const overviewCards = stats?.overview ? [
    { 
      icon: Users, 
      label: 'Registered Users', 
      value: stats.overview.total_students.toLocaleString(),
      color: 'bg-blue-500'
    },
    { 
      icon: School, 
      label: 'Total Schools', 
      value: stats.overview.total_schools,
      color: 'bg-green-500'
    },
    { 
      icon: BookOpen, 
      label: 'Total Enrollments', 
      value: stats.overview.total_enrollments.toLocaleString(),
      color: 'bg-indigo-500'
    },
    { 
      icon: Target, 
      label: 'Pass Rate', 
      value: `${stats.overview.pass_rate}%`,
      color: 'bg-purple-500'
    },
    { 
      icon: BarChart3, 
      label: 'Average Score', 
      value: `${stats.overview.average_score}%`,
      color: 'bg-orange-500'
    },
    { 
      icon: Award, 
      label: 'Exams Taken', 
      value: stats.overview.total_exams_taken.toLocaleString(),
      color: 'bg-teal-500'
    }
  ] : [];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-14">
        {/* Hero Banner */}
        <div className="bg-[#5f7c8a] py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-light text-white tracking-[0.15em]">
              STATISTICS DASHBOARD
            </h1>
            <div className="w-48 h-1 bg-white/50 mx-auto mt-4" />
            <p className="text-white/80 mt-4">Real-time insights and analytics</p>
          </div>
        </div>

        {/* Back Link & Refresh */}
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-xs text-gray-500">
                Last updated: {lastUpdated}
              </span>
            )}
            <button 
              onClick={fetchStatistics}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#5f7c8a] text-white rounded-lg hover:bg-[#4a6470] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="max-w-7xl mx-auto px-4 pb-16">
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw size={48} className="mx-auto mb-4 text-[#5f7c8a] animate-spin" />
                <p className="text-gray-600">Loading statistics...</p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="max-w-7xl mx-auto px-4 pb-16">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 mb-4">Error: {error}</p>
              <button 
                onClick={fetchStatistics}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Statistics Content */}
        {!loading && !error && stats && (
          <div className="max-w-7xl mx-auto px-4 pb-16">
            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {overviewCards.map((stat, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className={`p-2 rounded-lg ${stat.color} w-fit mb-2`}>
                      <stat.icon size={18} className="text-white" />
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Student Data Export Section */}
            <Card className="mb-8 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <FileSpreadsheet size={20} className="text-emerald-600" />
                    Export Student Data
                  </CardTitle>
                </div>
                <CardDescription>
                  Download enrolled and registered students with their assigned teachers
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="export-school" className="text-sm font-medium text-gray-700 mb-1 block">
                      Select School
                    </Label>
                    <Select value={exportSchool} onValueChange={setExportSchool}>
                      <SelectTrigger id="export-school" className="bg-white" data-testid="export-school-select">
                        <SelectValue placeholder="All Schools" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Schools</SelectItem>
                        {schools.map(school => (
                          <SelectItem key={school.id} value={school.id}>
                            {school.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => exportStudentsData('csv')}
                    disabled={exportLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="export-csv-btn"
                  >
                    {exportLoading ? (
                      <>
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        Export CSV
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => exportStudentsData('json')}
                    disabled={exportLoading}
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                    data-testid="export-json-btn"
                  >
                    <Download size={16} className="mr-2" />
                    Export JSON
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Export includes: Student ID, Name, Email, Phone, Branch, Constituency, Approval Status, Enrolled Schools, Assigned Teachers, Registration Date
                </p>
              </CardContent>
            </Card>

            {/* Exam Results Filter Section */}
            <Card className="mb-8 border-2 border-dashed border-[#5f7c8a]/30 bg-gradient-to-r from-slate-50 to-gray-50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Filter size={20} className="text-[#5f7c8a]" />
                    Exam Results Filter
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="ml-2 bg-[#5f7c8a] text-white">
                        Filters Active
                      </Badge>
                    )}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="text-[#5f7c8a] border-[#5f7c8a]"
                    data-testid="toggle-filters-btn"
                  >
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                </div>
                <CardDescription>Filter exam results by school, exam type, or date range</CardDescription>
              </CardHeader>
              
              {showFilters && (
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* School Filter */}
                    <div className="space-y-2">
                      <Label htmlFor="school-filter" className="text-sm font-medium text-gray-700">School</Label>
                      <Select value={filters.school_id} onValueChange={(v) => handleFilterChange('school_id', v)}>
                        <SelectTrigger id="school-filter" data-testid="school-filter">
                          <SelectValue placeholder="All Schools" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Schools</SelectItem>
                          {schools.map(school => (
                            <SelectItem key={school.id} value={school.id}>
                              {school.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Exam Type Filter */}
                    <div className="space-y-2">
                      <Label htmlFor="exam-type-filter" className="text-sm font-medium text-gray-700">Exam Type</Label>
                      <Select value={filters.exam_type} onValueChange={(v) => handleFilterChange('exam_type', v)}>
                        <SelectTrigger id="exam-type-filter" data-testid="exam-type-filter">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="mock">Mock Exams</SelectItem>
                          <SelectItem value="final">Final Exams</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Start Date */}
                    <div className="space-y-2">
                      <Label htmlFor="start-date" className="text-sm font-medium text-gray-700">Start Date</Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => handleFilterChange('start_date', e.target.value)}
                        className="w-full"
                        data-testid="start-date-filter"
                      />
                    </div>
                    
                    {/* End Date */}
                    <div className="space-y-2">
                      <Label htmlFor="end-date" className="text-sm font-medium text-gray-700">End Date</Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => handleFilterChange('end_date', e.target.value)}
                        className="w-full"
                        data-testid="end-date-filter"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={applyFilters}
                      disabled={loadingFiltered}
                      className="bg-[#5f7c8a] hover:bg-[#4a6470]"
                      data-testid="apply-filters-btn"
                    >
                      <Search size={16} className="mr-2" />
                      {loadingFiltered ? 'Searching...' : 'Apply Filters'}
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        onClick={clearFilters}
                        className="border-gray-300"
                        data-testid="clear-filters-btn"
                      >
                        <X size={16} className="mr-2" />
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Filtered Results Section */}
            {filteredStats && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Search size={24} className="text-[#5f7c8a]" />
                  Filtered Results
                  <Badge variant="outline" className="ml-2">
                    {filteredStats.summary.total_exams} exam{filteredStats.summary.total_exams !== 1 ? 's' : ''} found
                  </Badge>
                </h2>
                
                {/* Filtered Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{filteredStats.summary.total_exams}</p>
                      <p className="text-xs text-blue-600">Total Exams</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{filteredStats.summary.passed}</p>
                      <p className="text-xs text-green-600">Passed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-700">{filteredStats.summary.failed}</p>
                      <p className="text-xs text-red-600">Failed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{filteredStats.summary.pass_rate}%</p>
                      <p className="text-xs text-purple-600">Pass Rate</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-orange-700">{filteredStats.summary.average_score}%</p>
                      <p className="text-xs text-orange-600">Avg Score</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Filtered Results Table */}
                {filteredStats.results && filteredStats.results.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Detailed Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Student</TableHead>
                              <TableHead>Branch</TableHead>
                              <TableHead>School</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead className="text-center">Score</TableHead>
                              <TableHead className="text-center">Status</TableHead>
                              <TableHead>Submitted (EST)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredStats.results.slice(0, 50).map((result, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{result.student_name}</TableCell>
                                <TableCell className="text-sm text-gray-600">{result.branch}</TableCell>
                                <TableCell className="text-sm">{result.school_name?.substring(0, 25)}{result.school_name?.length > 25 ? '...' : ''}</TableCell>
                                <TableCell>
                                  <Badge variant={result.exam_type === 'final' ? 'default' : 'secondary'} 
                                         className={result.exam_type === 'final' ? 'bg-green-600' : 'bg-blue-500'}>
                                    {result.exam_type === 'final' ? 'Final' : 'Mock'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{result.score}%</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={result.passed ? 'default' : 'destructive'}
                                         className={result.passed ? 'bg-green-500' : ''}>
                                    {result.passed ? 'Passed' : 'Failed'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">{result.submitted_at_est}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {filteredStats.results.length > 50 && (
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            Showing first 50 of {filteredStats.results.length} results
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {filteredStats.results && filteredStats.results.length === 0 && (
                  <Card className="bg-gray-50">
                    <CardContent className="p-8 text-center">
                      <Search size={48} className="mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600">No exam results match your filter criteria.</p>
                      <p className="text-sm text-gray-500 mt-2">Try adjusting your filters.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Mock vs Final Exam Comparison Section */}
            {stats.mock_vs_final && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target size={24} className="text-[#5f7c8a]" />
                  Mock vs Final Exam Statistics
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Mock Exam Stats Card */}
                  <Card className="hover:shadow-lg transition-shadow border-l-4 border-blue-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                        <FileCheck size={20} className="text-blue-500" />
                        Mock Exams
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Taken</span>
                          <span className="text-2xl font-bold text-gray-800">{stats.mock_vs_final.mock.total}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Passed</span>
                          <span className="text-lg font-semibold text-green-600">{stats.mock_vs_final.mock.passed}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Failed</span>
                          <span className="text-lg font-semibold text-red-600">{stats.mock_vs_final.mock.failed}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Pass Rate</span>
                            <span className={`text-lg font-bold px-2 py-1 rounded ${
                              stats.mock_vs_final.mock.pass_rate >= 70 ? 'bg-green-100 text-green-700' : 
                              stats.mock_vs_final.mock.pass_rate >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-red-100 text-red-700'
                            }`}>
                              {stats.mock_vs_final.mock.pass_rate}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-gray-600">Avg Score</span>
                            <span className="text-lg font-semibold text-blue-600">{stats.mock_vs_final.mock.avg_score}%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Final Exam Stats Card */}
                  <Card className="hover:shadow-lg transition-shadow border-l-4 border-green-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                        <GraduationCap size={20} className="text-green-500" />
                        Final Exams
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Taken</span>
                          <span className="text-2xl font-bold text-gray-800">{stats.mock_vs_final.final.total}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Passed</span>
                          <span className="text-lg font-semibold text-green-600">{stats.mock_vs_final.final.passed}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Failed</span>
                          <span className="text-lg font-semibold text-red-600">{stats.mock_vs_final.final.failed}</span>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Pass Rate</span>
                            <span className={`text-lg font-bold px-2 py-1 rounded ${
                              stats.mock_vs_final.final.pass_rate >= 70 ? 'bg-green-100 text-green-700' : 
                              stats.mock_vs_final.final.pass_rate >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-red-100 text-red-700'
                            }`}>
                              {stats.mock_vs_final.final.pass_rate}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-gray-600">Avg Score</span>
                            <span className="text-lg font-semibold text-green-600">{stats.mock_vs_final.final.avg_score}%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Comparison Chart */}
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                        <BarChart3 size={20} className="text-[#5f7c8a]" />
                        Comparison
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart 
                          data={[
                            { name: 'Mock', passRate: stats.mock_vs_final.mock.pass_rate, avgScore: stats.mock_vs_final.mock.avg_score },
                            { name: 'Final', passRate: stats.mock_vs_final.final.pass_rate, avgScore: stats.mock_vs_final.final.avg_score }
                          ]}
                        >
                          <defs>
                            <linearGradient id="mockPassGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#22c55e" stopOpacity={1}/>
                              <stop offset="100%" stopColor="#16a34a" stopOpacity={0.8}/>
                            </linearGradient>
                            <linearGradient id="mockAvgGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity={1}/>
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" />
                          <YAxis domain={[0, 100]} unit="%" />
                          <Tooltip 
                            formatter={(value) => `${value}%`}
                            contentStyle={{ borderRadius: '8px' }}
                          />
                          <Legend />
                          <Bar dataKey="passRate" name="Pass Rate" fill="url(#mockPassGradient)" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="avgScore" name="Avg Score" fill="url(#mockAvgGradient)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
              {/* Registrations by Branch - Pie Chart */}
              <Card className="hover:shadow-xl transition-shadow border-2 border-gradient-to-r from-pink-200 to-blue-200">
                <CardHeader className="bg-gradient-to-r from-pink-50 to-blue-50">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Users size={20} className="text-pink-500" />
                    Total Registrations by Branch
                  </CardTitle>
                  <p className="text-sm text-gray-500">Distribution of students across church branches</p>
                </CardHeader>
                <CardContent className="pt-4">
                  {stats.registrations_by_branch && stats.registrations_by_branch.length > 0 ? (
                    <ResponsiveContainer width="100%" height={700}>
                      <PieChart>
                        <Pie
                          data={stats.registrations_by_branch}
                          cx="50%"
                          cy="45%"
                          innerRadius={80}
                          outerRadius={160}
                          paddingAngle={1}
                          dataKey="value"
                          label={({ name, percent, value }) => {
                            const pct = (percent * 100).toFixed(1);
                            const displayName = name.length > 8 ? name.slice(0, 8) + '..' : name;
                            return `${displayName} ${value} (${pct}%)`;
                          }}
                          labelLine={{ stroke: '#999', strokeWidth: 0.5 }}
                          style={{ fontSize: '7px', fontWeight: '500' }}
                        >
                          {stats.registrations_by_branch.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                              stroke="#fff"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          content={<CustomTooltip />}
                          wrapperStyle={{ zIndex: 100 }}
                        />
                        <Legend 
                          layout="horizontal"
                          align="center"
                          verticalAlign="bottom"
                          wrapperStyle={{ fontSize: '7px', paddingTop: '5px' }}
                          formatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[700px] flex items-center justify-center text-gray-500">
                      <p>No registration data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Branch Registrations by School - Bar Chart */}
              <Card className="hover:shadow-xl transition-shadow border-2 border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <School size={20} className="text-purple-500" />
                    Branch Enrollments in Lay Schools
                  </CardTitle>
                  <p className="text-sm text-gray-500">Real-time data - updates automatically with each enrollment</p>
                </CardHeader>
                <CardContent className="pt-4">
                  {stats.branch_school_enrollments && stats.branch_school_enrollments.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(550, stats.branch_school_enrollments.length * 22)}>
                      <BarChart 
                        data={stats.branch_school_enrollments} 
                        layout="vertical"
                        margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e0e0e0" />
                        <XAxis type="number" tick={{ fontSize: 9 }} />
                        <YAxis 
                          dataKey="branch" 
                          type="category" 
                          width={95} 
                          tick={{ fontSize: 8 }}
                          interval={0}
                          tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '..' : value}
                        />
                        <Tooltip content={<BranchEnrollmentTooltip />} />
                        <Bar 
                          dataKey="count" 
                          name="Enrollments" 
                          radius={[0, 6, 6, 0]}
                          label={{ position: 'right', fontSize: 8, fill: '#333', fontWeight: 'bold' }}
                        >
                          {stats.branch_school_enrollments.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]}
                              stroke="#fff"
                              strokeWidth={1}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[550px] flex items-center justify-center text-gray-500">
                      <p>No branch enrollment data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Branch-School Enrollment Table */}
            <Card className="hover:shadow-xl transition-shadow mb-6 border-2 border-emerald-200">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                  <TrendingUp size={20} className="text-emerald-500" />
                  Branch Registrations for Lay Schools (Detailed)
                </CardTitle>
                <p className="text-sm text-gray-500">Real-time breakdown - auto-updates with each enrollment</p>
              </CardHeader>
              <CardContent className="pt-4">
                {stats.branch_school_enrollments && stats.branch_school_enrollments.length > 0 ? (
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b-2 border-emerald-200">
                          <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs">Branch</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs">School</th>
                          <th className="text-center py-2 px-3 font-semibold text-gray-700 text-xs">Enrolled</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.branch_school_enrollments.map((item, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-emerald-50 transition-colors">
                            <td className="py-2 px-3 font-medium text-gray-800 text-xs">
                              <div className="flex items-center gap-2">
                                <span 
                                  className="w-3 h-3 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                />
                                {item.branch.length > 15 ? item.branch.slice(0, 15) + '...' : item.branch}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-gray-600 text-xs">
                              {item.fullSchoolName.length > 20 ? item.fullSchoolName.slice(0, 20) + '...' : item.fullSchoolName}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span 
                                className="inline-flex items-center justify-center w-7 h-7 text-white rounded-full font-bold text-xs"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              >
                                {item.count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    <p>No branch enrollment data available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Constituency Enrollment Histogram */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
              {/* Constituency Registrations - Histogram */}
              <Card className="hover:shadow-xl transition-shadow border-2 border-orange-200">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <MapPin size={20} className="text-orange-500" />
                    Registrations by Constituency
                  </CardTitle>
                  <p className="text-sm text-gray-500">Real-time distribution of students by constituency</p>
                </CardHeader>
                <CardContent className="pt-4">
                  {stats.registrations_by_constituency && stats.registrations_by_constituency.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart 
                        data={stats.registrations_by_constituency} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <defs>
                          <linearGradient id="constituencyGradient1" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FF6384" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#FF6384" stopOpacity={0.6}/>
                          </linearGradient>
                          <linearGradient id="constituencyGradient2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#36A2EB" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#36A2EB" stopOpacity={0.6}/>
                          </linearGradient>
                          <linearGradient id="constituencyGradient3" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FFCE56" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#FFCE56" stopOpacity={0.6}/>
                          </linearGradient>
                          <linearGradient id="constituencyGradient4" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4BC0C0" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#4BC0C0" stopOpacity={0.6}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 9, fill: '#666' }}
                          angle={-35}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-3 rounded-lg shadow-lg border">
                                  <p className="font-semibold text-gray-800">{payload[0].payload.name}</p>
                                  <p className="text-orange-600 font-bold">{payload[0].value} students</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          name="Students"
                          radius={[8, 8, 0, 0]}
                          label={{ position: 'top', fontSize: 11, fill: '#333', fontWeight: 'bold' }}
                        >
                          {stats.registrations_by_constituency.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`url(#constituencyGradient${(index % 4) + 1})`}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-gray-500">
                      <p>No constituency data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Constituency School Enrollments - Stacked */}
              <Card className="hover:shadow-xl transition-shadow border-2 border-cyan-200">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-sky-50">
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <School size={20} className="text-cyan-500" />
                    Constituency Enrollments by School
                  </CardTitle>
                  <p className="text-sm text-gray-500">Which constituencies are enrolled in which schools</p>
                </CardHeader>
                <CardContent className="pt-4">
                  {stats.constituency_school_enrollments && stats.constituency_school_enrollments.length > 0 ? (
                    <div className="space-y-4">
                      {stats.constituency_school_enrollments.map((item, index) => (
                        <div key={item.constituency} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-semibold text-gray-800 flex items-center gap-2">
                              <span 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              {item.constituency}
                            </span>
                            <span 
                              className="px-3 py-1 rounded-full text-white font-bold text-sm"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            >
                              {item.totalEnrolled} enrolled
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(item.schools).map(([school, count]) => (
                              <span 
                                key={school}
                                className="text-xs px-2 py-1 rounded-full bg-white border shadow-sm"
                              >
                                {school.length > 15 ? school.slice(0, 15) + '...' : school}: <strong>{count}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-gray-500">
                      <p>No constituency enrollment data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Exam Pass/Fail Rates by School - Bar Chart */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Target size={20} className="text-[#5f7c8a]" />
                    Exam Pass/Fail Rates by School
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.exam_stats_by_school && stats.exam_stats_by_school.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.exam_stats_by_school} layout="vertical">
                        <defs>
                          <linearGradient id="passGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#86efac" stopOpacity={0.9}/>
                          </linearGradient>
                          <linearGradient id="failGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#fca5a5" stopOpacity={0.9}/>
                            <stop offset="100%" stopColor="#ef4444" stopOpacity={1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" domain={[0, 100]} unit="%" />
                        <YAxis dataKey="school" type="category" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="passRate" name="Pass Rate" fill="url(#passGradient)" stackId="a" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="failRate" name="Fail Rate" fill="url(#failGradient)" stackId="a" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <p>No exam data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Enrollments by School - Horizontal Bar Chart */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <School size={20} className="text-[#5f7c8a]" />
                    Enrollments by School
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.enrollments_by_school && stats.enrollments_by_school.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.enrollments_by_school} layout="vertical">
                        <defs>
                          {stats.enrollments_by_school.map((_, index) => (
                            <linearGradient key={`enrollGrad${index}`} id={`enrollGradient${index}`} x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.8}/>
                              <stop offset="100%" stopColor={COLORS[(index + 3) % COLORS.length]} stopOpacity={1}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" />
                        <YAxis dataKey="school" type="category" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="enrollments" name="Enrollments" radius={[0, 4, 4, 0]}>
                          {stats.enrollments_by_school.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#enrollGradient${index})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <p>No enrollment data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Average Scores by School - Bar Chart */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <BarChart3 size={20} className="text-[#5f7c8a]" />
                    Average Scores by School
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.exam_stats_by_school && stats.exam_stats_by_school.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.exam_stats_by_school}>
                        <defs>
                          {stats.exam_stats_by_school.map((_, index) => (
                            <linearGradient key={`scoreGrad${index}`} id={`scoreGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={COLORS[(index + 6) % COLORS.length]} stopOpacity={1}/>
                              <stop offset="100%" stopColor={COLORS[(index + 9) % COLORS.length]} stopOpacity={0.7}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="school" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} height={80} />
                        <YAxis domain={[0, 100]} unit="%" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="avgScore" name="Avg Score" radius={[4, 4, 0, 0]}>
                          {stats.exam_stats_by_school.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={`url(#scoreGradient${index})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <p>No score data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 3 - Full Width Line Charts */}
            <div className="grid grid-cols-1 gap-6">
              {/* Enrollment Trends Over Time - Area Chart */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <TrendingUp size={20} className="text-[#5f7c8a]" />
                    Enrollment Trends Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.enrollment_trends && stats.enrollment_trends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={stats.enrollment_trends}>
                        <defs>
                          <linearGradient id="colorEnrollments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                            <stop offset="50%" stopColor="#8b5cf6" stopOpacity={0.5}/>
                            <stop offset="95%" stopColor="#a855f7" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorNewEnrollments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#86efac" stopOpacity={0.2}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="enrollments" 
                          name="Total Enrollments" 
                          stroke="#6366f1" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorEnrollments)" 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="newEnrollments" 
                          name="New Enrollments" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorNewEnrollments)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <p>No trend data available yet. Enrollments will appear here over time.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Exam Performance Trends - Line Chart */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Award size={20} className="text-[#5f7c8a]" />
                    Exam Performance Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.exam_trends && stats.exam_trends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.exam_trends}>
                        <defs>
                          <linearGradient id="totalExamsGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#8b5cf6" />
                          </linearGradient>
                          <linearGradient id="passedGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#22c55e" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="total" 
                          name="Total Exams" 
                          stroke="url(#totalExamsGradient)" 
                          strokeWidth={3}
                          dot={{ r: 5, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 7, fill: '#8b5cf6' }}
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="passed" 
                          name="Passed" 
                          stroke="url(#passedGradient)" 
                          strokeWidth={3}
                          dot={{ r: 5, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 7, fill: '#10b981' }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="avgScore" 
                          name="Avg Score" 
                          stroke="#f59e0b" 
                          strokeWidth={3}
                          strokeDasharray="5 5"
                          dot={{ r: 5, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 7, fill: '#fbbf24' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <p>No exam trend data available yet. Take some exams to see trends!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats Table */}
            {stats.exam_stats_by_school && stats.exam_stats_by_school.length > 0 && (
              <Card className="mt-6 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Detailed School Performance (All Exams)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">School</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Total Exams</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Passed</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Failed</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Pass Rate</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Avg Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.exam_stats_by_school.map((school, index) => (
                          <tr key={index} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-800">{school.fullName}</td>
                            <td className="text-center py-3 px-4">{school.totalExams}</td>
                            <td className="text-center py-3 px-4 text-green-600">{school.passed}</td>
                            <td className="text-center py-3 px-4 text-red-600">{school.failed}</td>
                            <td className="text-center py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                school.passRate >= 70 ? 'bg-green-100 text-green-700' : 
                                school.passRate >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-red-100 text-red-700'
                              }`}>
                                {school.passRate}%
                              </span>
                            </td>
                            <td className="text-center py-3 px-4">{school.avgScore}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Detailed Performance by School AND Exam Type */}
            {stats.exam_stats_by_school_and_type && stats.exam_stats_by_school_and_type.length > 0 && (
              <Card className="mt-6 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                    <Target size={20} className="text-[#5f7c8a]" />
                    School Performance by Exam Type (Mock vs Final)
                  </CardTitle>
                  <p className="text-sm text-gray-500">Detailed breakdown showing mock and final exam performance for each school</p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">School</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Exam Type</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Total</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Passed</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Failed</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Pass Rate</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">Avg Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.exam_stats_by_school_and_type.map((item, index) => (
                          <tr key={index} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-800">{item.fullName}</td>
                            <td className="text-center py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                item.examType === 'final' 
                                  ? 'bg-green-100 text-green-700 border border-green-300' 
                                  : 'bg-blue-100 text-blue-700 border border-blue-300'
                              }`}>
                                {item.examType === 'final' ? '🎓 Final' : '📝 Mock'}
                              </span>
                            </td>
                            <td className="text-center py-3 px-4 font-medium">{item.totalExams}</td>
                            <td className="text-center py-3 px-4 text-green-600 font-medium">{item.passed}</td>
                            <td className="text-center py-3 px-4 text-red-600 font-medium">{item.failed}</td>
                            <td className="text-center py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.passRate >= 70 ? 'bg-green-100 text-green-700' : 
                                item.passRate >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                                'bg-red-100 text-red-700'
                              }`}>
                                {item.passRate}%
                              </span>
                            </td>
                            <td className="text-center py-3 px-4 font-medium">{item.avgScore}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendance Statistics Section */}
            <Card className="mt-6 hover:shadow-lg transition-shadow border-2 border-orange-200">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
                <CardTitle className="text-lg font-medium text-orange-800 flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-600" />
                  Attendance Alerts - Students with Poor Attendance
                </CardTitle>
                <CardDescription className="text-orange-600">
                  Students who have missed more than 2 classes in the last 8 weeks
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {loadingAttendance ? (
                  <div className="text-center py-8">
                    <RefreshCw className="mx-auto animate-spin text-orange-500" size={32} />
                    <p className="text-gray-500 mt-2">Loading attendance data...</p>
                  </div>
                ) : !attendanceStats ? (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardCheck size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No attendance data available</p>
                    <p className="text-sm mt-2">Start tracking attendance from school pages to see statistics here</p>
                  </div>
                ) : attendanceStats.poor_attendance_count === 0 ? (
                  <div className="text-center py-8 bg-green-50 rounded-lg">
                    <div className="text-green-600 mb-2">
                      <ClipboardCheck size={48} className="mx-auto" />
                    </div>
                    <p className="text-green-700 font-medium">All students have good attendance!</p>
                    <p className="text-sm text-green-600 mt-1">
                      No students have missed more than 2 classes in the last 8 weeks
                    </p>
                    <p className="text-xs text-gray-500 mt-3">
                      Period: {attendanceStats.period?.start_date} to {attendanceStats.period?.end_date}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-600 mb-1">
                          <AlertTriangle size={18} />
                          <span className="font-medium">Students at Risk</span>
                        </div>
                        <p className="text-3xl font-bold text-red-700">{attendanceStats.poor_attendance_count}</p>
                        <p className="text-xs text-red-500">More than 2 absences</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-blue-600 mb-1">
                          <Calendar size={18} />
                          <span className="font-medium">Monitoring Period</span>
                        </div>
                        <p className="text-lg font-bold text-blue-700">{attendanceStats.period?.weeks} Weeks</p>
                        <p className="text-xs text-blue-500">{attendanceStats.period?.start_date} - {attendanceStats.period?.end_date}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <ClipboardCheck size={18} />
                          <span className="font-medium">Threshold</span>
                        </div>
                        <p className="text-lg font-bold text-gray-700">&gt; 2 Absences</p>
                        <p className="text-xs text-gray-500">Within 8 weeks period</p>
                      </div>
                    </div>

                    {/* Students Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-orange-50">
                            <TableHead className="font-medium">Student</TableHead>
                            <TableHead className="font-medium">Contact</TableHead>
                            <TableHead className="font-medium">Branch</TableHead>
                            <TableHead className="font-medium">School</TableHead>
                            <TableHead className="text-center font-medium">Sessions</TableHead>
                            <TableHead className="text-center font-medium">Present</TableHead>
                            <TableHead className="text-center font-medium">Absent</TableHead>
                            <TableHead className="text-center font-medium">Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {attendanceStats.students_with_poor_attendance.map((student, idx) => (
                            <TableRow key={idx} className="hover:bg-orange-50">
                              <TableCell>
                                <div>
                                  <p className="font-medium">{student.student_name}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p>{student.student_email}</p>
                                  <p className="text-gray-500">{student.student_phone}</p>
                                </div>
                              </TableCell>
                              <TableCell>{student.student_branch || '-'}</TableCell>
                              <TableCell className="font-medium">{student.school_name}</TableCell>
                              <TableCell className="text-center">{student.total_sessions}</TableCell>
                              <TableCell className="text-center text-green-600 font-medium">{student.present}</TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-red-500">{student.absent}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${
                                  student.attendance_rate >= 80 ? 'bg-green-100 text-green-700' :
                                  student.attendance_rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {student.attendance_rate}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

const StatisticsPage = () => {
  return (
    <AdminProtected title="Statistics Dashboard">
      <StatisticsContent />
    </AdminProtected>
  );
};

export default StatisticsPage;
